import React, { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { formatNumberSmart } from '@/lib/utils'

const steps = [
  { id: 1, label: 'Starting point' },
  { id: 2, label: 'Goal' },
  { id: 3, label: 'Review' },
]

const percent = (num) => (num === '' ? '' : Math.max(0, Math.min(100, Number(num) || 0)))
const pounds = (num) => (num === '' ? '' : Math.max(0, Math.min(600, Number(num) || 0)))
const poundsSigned = (num, min = -50, max = 50) => (num === '' ? '' : Math.max(min, Math.min(max, Number(num) || 0)))

const toISODate = (d) => {
  const date = d instanceof Date ? d : new Date(d)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const computeLeanAndFat = (weight, bodyfatPct) => {
  const w = Number(weight) || 0
  const bf = Number(bodyfatPct) || 0
  const fatMass = (w * bf) / 100
  const leanMass = w - fatMass
  return { fatMass, leanMass }
}

const defaultBodyfatClasses = [
  { classification: 'Essential Fat', range: '0% - 11.5%', min: 0, max: 11.5 },
  { classification: 'Optimal', range: '11.6% - 19%', min: 11.6, max: 19 },
  { classification: 'Moderate', range: '19.1% - 25%', min: 19.1, max: 25 },
  { classification: 'High', range: '25.1% - 33%', min: 25.1, max: 33 },
  { classification: 'Very high', range: 'Above 33%', min: 33, max: Infinity },
]

const normalizeBodyfatClasses = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.rows)) return data.rows
  if (Array.isArray(data?.data)) return data.data
  if (data && typeof data === 'object') {
    const vals = Object.values(data).filter((v) => v && typeof v === 'object')
    if (vals.length) return vals
  }
  return []
}

const deriveRangeNumbers = (row, fallback) => {
  const fallbackMin = fallback?.min ?? null
  const fallbackMax = fallback?.max ?? null
  let min = row?.min ?? row?.range_min ?? fallbackMin
  let max = row?.max ?? row?.range_max ?? fallbackMax
  const rangeText = (row?.range || fallback?.range || '').toString().toLowerCase()
  if (min == null || max == null || !Number.isFinite(min) || (!Number.isFinite(max) && max !== Infinity)) {
    const nums = (rangeText.match(/[\d.]+/g) || []).map((n) => Number(n))
    if (nums.length >= 2) {
      min = min ?? nums[0]
      max = max ?? nums[1]
    } else if (nums.length === 1) {
      if (rangeText.includes('above') || rangeText.includes('+') || rangeText.includes('>')) {
        min = min ?? nums[0]
        max = max ?? Infinity
      } else {
        min = min ?? 0
        max = max ?? nums[0]
      }
    }
  }
  if (rangeText.includes('above') && (max == null || max === fallbackMax)) max = Infinity
  return {
    min: Number.isFinite(min) || min === Infinity ? min : fallbackMin,
    max: Number.isFinite(max) || max === Infinity ? max : fallbackMax,
  }
}

const numberOrZero = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

// "2026-10-10" from <input type="date"> is a local day; new Date() would read it
// as UTC midnight and show the previous day in US time zones.
const parseLocalDate = (value) => {
  if (value instanceof Date) return value
  const match = typeof value === 'string' && value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value)
}

const formatDisplayDate = (value) => {
  const date = parseLocalDate(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(date)
}

const addWeeksToDate = (isoDate, weeks) => {
  const date = parseLocalDate(isoDate)
  if (Number.isNaN(date.getTime())) return ''
  const result = new Date(date)
  result.setDate(result.getDate() + weeks * 7)
  return result
}

const NumberField = ({ id, label, unit, value, onChange, hint, min, max, step = 'any' }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-medium text-slate-700">{label}</label>
    <div className="relative">
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={value}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value)}
        className="pr-10 tabular-nums"
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">{unit}</span>
    </div>
    {hint && <p className="text-[13px] text-slate-500">{hint}</p>}
  </div>
)

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 px-4 py-3">
    <p className="text-[13px] text-slate-500">{label}</p>
    <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">{value}</p>
  </div>
)

const signed = (value, unit) => {
  const n = Number(value) || 0
  const text = `${formatNumberSmart(Math.abs(n), 2)}${unit}`
  return n > 0 ? `+${text}` : n < 0 ? `−${text}` : text
}

const NewProgramWizard = ({ open, onOpenChange, clientId, onFinished }) => {
  const { companyUrl } = useAuthStore()
  const [activeStep, setActiveStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [clientInfo, setClientInfo] = useState(null)
  const [bodyfatClasses, setBodyfatClasses] = useState(defaultBodyfatClasses)
  const [startDate, setStartDate] = useState(() => toISODate(new Date()))
  const [startWeight, setStartWeight] = useState(0)
  const [startBodyfat, setStartBodyfat] = useState(0)
  const [goalBodyfat, setGoalBodyfat] = useState(0)
  const [goalMuscleGain, setGoalMuscleGain] = useState(0)
  const [estimatedWeeks, setEstimatedWeeks] = useState(0)
  const [estimationData, setEstimationData] = useState(null)
  const [selectedPackage, setSelectedPackage] = useState('')

  const gender = useMemo(() => {
    const g = clientInfo?.personal?.gender || clientInfo?.gender
    if (!g) return 'm'
    return g.toLowerCase().startsWith('f') ? 'f' : 'm'
  }, [clientInfo])

  const userAge = useMemo(() => {
    const bd = clientInfo?.personal?.birthdate || clientInfo?.birthdate
    if (!bd) return 30
    const d = new Date(bd)
    if (Number.isNaN(d.getTime())) return 30
    const diff = Date.now() - d.getTime()
    const ageDt = new Date(diff)
    return Math.abs(ageDt.getUTCFullYear() - 1970) || 30
  }, [clientInfo])

  useEffect(() => {
    if (!open || !companyUrl || !clientId) return
    const controller = new AbortController()
    const load = async () => {
      setBusy(true)
      setError('')
      try {
        const clientRes = await apiService.post(`/${companyUrl}/getclients`, { user_id: String(clientId) }, controller.signal)
        const clientRecord = clientRes.success ? (clientRes.data?.rows?.[0] || clientRes.data?.[0] || clientRes.data) : null
        setClientInfo(clientRecord)
        const bfcPayload = { client_id: String(clientId), gender: (clientRecord?.personal?.gender || clientRecord?.gender || 'm').toString().charAt(0).toLowerCase(), user_age: userAge }
        const bfcRes = await apiService.post(`/${companyUrl}/getbodyfatclassifications`, bfcPayload, controller.signal)
        if (bfcRes.success) {
          const normalized = normalizeBodyfatClasses(bfcRes.data)
          setBodyfatClasses(normalized.length ? normalized : defaultBodyfatClasses)
        } else {
          setBodyfatClasses(defaultBodyfatClasses)
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Failed to load initial data for program creation')
      } finally {
        setBusy(false)
      }
    }
    load()
    return () => controller.abort()
  }, [open, companyUrl, clientId, userAge])

  useEffect(() => {
    if (!open) {
      setActiveStep(1)
      setError('')
      setEstimatedWeeks(0)
      setEstimationData(null)
      setBodyfatClasses(defaultBodyfatClasses)
      setStartDate(toISODate(new Date()))
      setStartWeight(0)
      setStartBodyfat(0)
      setGoalBodyfat(0)
      setGoalMuscleGain(0)
      setSelectedPackage('')
      setBodyfatClasses(defaultBodyfatClasses)
    }
  }, [open])

  const massesStart = useMemo(() => computeLeanAndFat(startWeight, startBodyfat), [startWeight, startBodyfat])

  const renderedBodyfatClasses = useMemo(() => {
    const src = Array.isArray(bodyfatClasses) && bodyfatClasses.length ? bodyfatClasses : defaultBodyfatClasses
    return (src.length ? src : defaultBodyfatClasses).map((row, idx) => {
      const fallback = defaultBodyfatClasses[idx] || defaultBodyfatClasses[defaultBodyfatClasses.length - 1]
      const { min, max } = deriveRangeNumbers(row, fallback)
      const rangeVal = row?.range || (min != null && max != null ? (max === Infinity ? `Above ${min}%` : `${min}% - ${max}%`) : null)
      const classification =
        row?.classification ||
        row?.name ||
        row?.current_classification ||
        row?.title ||
        row?.label ||
        fallback?.classification ||
        '-'
      const range = rangeVal || fallback?.range || '-'
      return { classification, range, min, max }
    })
  }, [bodyfatClasses])

  const summary = useMemo(() => {
    const startFat = massesStart.fatMass
    const startLean = massesStart.leanMass
    // getestimatedweeksforgoal returns the goal as { goal: { weight, leanmass, bodyfatinpounds } };
    // use the server's numbers so the review matches what's saved.
    const serverGoal = estimationData?.goal || {}
    const goalLean = numberOrZero(estimationData?.goal_lean_mass ?? serverGoal.leanmass ?? startLean + numberOrZero(goalMuscleGain))
    const goalWeightApi = numberOrZero(estimationData?.goal_weight ?? serverGoal.weight)
    const goalWeightDerived = percent(goalBodyfat) < 100 && percent(goalBodyfat) > 0 ? goalLean / (1 - percent(goalBodyfat) / 100) : goalLean
    const goalWeight = goalWeightApi || goalWeightDerived
    const goalFat = numberOrZero(estimationData?.goal_fat_mass ?? serverGoal.bodyfatinpounds ?? goalWeight - goalLean)
    const weightChange = numberOrZero(estimationData?.weight_change ?? goalWeight - startWeight)
    const leanChange = numberOrZero(estimationData?.lean_mass_change ?? goalLean - startLean)
    const fatChange = numberOrZero(estimationData?.fat_mass_change ?? goalFat - startFat)
    const bodyFatChange = numberOrZero(estimationData?.bodyfat_change ?? percent(goalBodyfat) - percent(startBodyfat))
    const startDateDisplay = formatDisplayDate(startDate)
    const estimatedEnd = estimationData?.estimated_end_date
      ? formatDisplayDate(estimationData.estimated_end_date)
      : formatDisplayDate(addWeeksToDate(startDate, estimatedWeeks))

    return {
      startFat,
      startLean,
      goalLean,
      goalWeight,
      goalFat,
      weightChange,
      leanChange,
      fatChange,
      bodyFatChange,
      startDateDisplay,
      estimatedEnd,
    }
  }, [massesStart, goalMuscleGain, goalBodyfat, startBodyfat, startWeight, estimationData, estimatedWeeks, startDate])

  const goNext = async () => {
    setError('')
    if (activeStep === 1) {
      if ((Number(startWeight) || 0) <= 0) {
        setError('Start weight must be greater than 0')
        return
      }
      setActiveStep(2)
      return
    }

    if (activeStep === 2) {
      setBusy(true)
      try {
        const payload = {
          start_weight: String(startWeight),
          start_bodyfat: String(startBodyfat),
          goal_bodyfat: String(goalBodyfat),
          goal_muscle_gain_pounds: String(goalMuscleGain),
          user_age: userAge,
          gender,
          client_id: String(clientId),
        }
        const res = await apiService.post(`/${companyUrl}/getestimatedweeksforgoal`, payload)
        if (res.success) {
          const weeks = Number(res.data?.estimated_weeks || res.data?.weeks || res.data?.weekstogoal || 0)
          setEstimatedWeeks(Number.isFinite(weeks) ? weeks : 0)
          setEstimationData(res.data || null)
          setActiveStep(3)
        } else {
          setError(res.error || 'Failed to estimate weeks for goal')
        }
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') setError('Failed to estimate weeks for goal')
      } finally {
        setBusy(false)
      }
      return
    }
  }

  const goBack = () => {
    setError('')
    setActiveStep((s) => Math.max(1, s - 1))
  }

  useEffect(() => {
    if (activeStep === 3) {
      const options = [4, 8, 12, 16, 20, 24]
      const nearest = options.reduce((prev, cur) => (Math.abs(cur - estimatedWeeks) < Math.abs(prev - estimatedWeeks) ? cur : prev), options[0])
      setSelectedPackage(String(nearest))
    }
  }, [activeStep, estimatedWeeks])

  const createProgram = async () => {
    setBusy(true)
    setError('')
    try {
      const payload = {
        user_id: String(clientId),
        start_weight: String(startWeight),
        start_bodyfat: String(startBodyfat),
        goal_bodyfat: String(goalBodyfat),
        goal_muscle_gain_pounds: String(goalMuscleGain),
        user_age: userAge,
        gender,
        packages_purchased: String(selectedPackage || 0),
        // The date picked on step 1 (YYYY-MM-DD); programs come back with start_date.
        start_date: startDate,
      }
      const saveRes = await apiService.post(`/${companyUrl}/savegoal`, payload)
      if (!saveRes.success) {
        setError(saveRes.error || 'Failed to save goal')
        setBusy(false)
        return
      }

      const programId = saveRes.data?.program_id || saveRes.data?.id || saveRes.data?.goal?.program_id
      await apiService.post(`/${companyUrl}/getclientprogramswithgoals`, { client_id: String(clientId), program_id: programId ? String(programId) : undefined })

      onOpenChange(false)
      if (onFinished) onFinished()
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') setError('Failed to create program')
    } finally {
      setBusy(false)
    }
  }

  const stepLabel = steps.find((st) => st.id === activeStep)?.label
  const clientName = [clientInfo?.firstname, clientInfo?.lastname].filter(Boolean).join(' ')
  const startPct = percent(startBodyfat)
  const goalPct = percent(goalBodyfat)
  const inRow = (row, value) =>
    value !== '' && row.min != null && value >= row.min && (row.max == null || row.max === Infinity || value <= row.max)

  const reviewRows = [
    { label: 'Weight', start: `${formatNumberSmart(startWeight, 2)} lb`, goal: `${formatNumberSmart(summary.goalWeight, 2)} lb`, change: signed(summary.weightChange, ' lb') },
    { label: 'Body fat', start: `${formatNumberSmart(startPct, 2)}%`, goal: `${formatNumberSmart(goalPct, 2)}%`, change: signed(summary.bodyFatChange, '%') },
    { label: 'Lean mass', start: `${formatNumberSmart(summary.startLean, 2)} lb`, goal: `${formatNumberSmart(summary.goalLean, 2)} lb`, change: signed(summary.leanChange, ' lb') },
    { label: 'Fat mass', start: `${formatNumberSmart(summary.startFat, 2)} lb`, goal: `${formatNumberSmart(summary.goalFat, 2)} lb`, change: signed(summary.fatChange, ' lb') },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New program</DialogTitle>
          <DialogDescription>
            {clientName ? `For ${clientName}. ` : ''}Step {activeStep} of {steps.length}: {stepLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-1.5" aria-hidden="true">
          {steps.map((st) => (
            <div key={st.id} className={`h-1 rounded-full ${activeStep >= st.id ? 'bg-blue-600' : 'bg-slate-200'}`} />
          ))}
        </div>

        {error && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        {activeStep === 1 && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <label htmlFor="program-start-date" className="block text-sm font-medium text-slate-700">Start date</label>
                <Input id="program-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <NumberField id="start-weight" label="Weight" unit="lb" min={0} max={600} value={startWeight} onChange={(val) => setStartWeight(pounds(val))} />
              <NumberField id="start-bodyfat" label="Body fat" unit="%" min={0} max={70} step={0.5} value={startBodyfat} onChange={(val) => setStartBodyfat(percent(val))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Fat mass" value={`${formatNumberSmart(massesStart.fatMass, 2)} lb`} />
              <Stat label="Lean mass" value={`${formatNumberSmart(massesStart.leanMass, 2)} lb`} />
            </div>
          </div>
        )}

        {activeStep === 2 && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <NumberField id="goal-bodyfat" label="Goal body fat" unit="%" min={0} max={70} step={0.5} value={goalBodyfat} onChange={(val) => setGoalBodyfat(percent(val))} />
              <NumberField
                id="goal-muscle"
                label="Muscle to gain"
                unit="lb"
                min={-50}
                max={50}
                value={goalMuscleGain}
                onChange={(val) => setGoalMuscleGain(poundsSigned(val))}
                hint="Use a negative number for a planned loss."
              />
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">
                Body fat ranges <span className="font-normal text-slate-500">for a {userAge}-year-old {gender === 'f' ? 'woman' : 'man'}</span>
              </p>
              <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
                {renderedBodyfatClasses.map((row, idx) => {
                  const isNow = inRow(row, startPct)
                  const isGoal = inRow(row, goalPct) && goalPct > 0
                  return (
                    <li key={idx} className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${isNow || isGoal ? 'bg-blue-50/60' : ''}`}>
                      <span className="font-medium text-slate-900">{row.classification}</span>
                      <span className="flex items-center gap-2">
                        {isNow && <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white">Now</span>}
                        {isGoal && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">Goal</span>}
                        <span className="w-24 text-right tabular-nums text-slate-600">{row.range}</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )}

        {activeStep === 3 && (
          <div className="space-y-5">
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[13px] text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium"><span className="sr-only">Measure</span></th>
                    <th className="px-4 py-2 text-right font-medium">Start</th>
                    <th className="px-4 py-2 text-right font-medium">Goal</th>
                    <th className="px-4 py-2 text-right font-medium">Change</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {reviewRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row" className="px-4 py-2.5 text-left font-medium text-slate-900">{row.label}</th>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{row.start}</td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-slate-900">{row.goal}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{row.change}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label="Start date" value={summary.startDateDisplay || startDate} />
              <Stat label="Estimated end" value={summary.estimatedEnd || '–'} />
              <Stat label="Sessions to goal" value={`${estimatedWeeks}`} />
            </div>

            <div className="space-y-1.5 sm:w-1/2">
              <label className="block text-sm font-medium text-slate-700">Package</label>
              <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select package" />
                </SelectTrigger>
                <SelectContent>
                  {[4, 8, 12, 16, 20, 24].map((w) => (
                    <SelectItem key={w} value={String(w)}>{w} weeks</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-200 pt-4">
          <Button variant="ghost" onClick={goBack} disabled={activeStep === 1 || busy}>Back</Button>
          {activeStep < 3 ? (
            <Button onClick={goNext} disabled={busy} aria-busy={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Next
            </Button>
          ) : (
            <Button onClick={createProgram} disabled={busy} aria-busy={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Create program
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default NewProgramWizard
